import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { GeneralAvailability } from '@core/models';
import {
  AvailabilityService,
  DvmService,
  TutorialService,
} from '@core/services';
import { Subscription } from 'rxjs';
import { BestAvailableService } from '../../services/best-available.service';
import { Popover3dviewService } from '../../services/popover-3dview.service';

@Component({
  standalone: false,
  selector: 'app-section-popover',
  templateUrl: './section-popover.component.html',
  styleUrls: ['./section-popover.component.css'],
})
export class SectionPopoverComponent implements OnInit, OnDestroy {
  private availabilityService = inject(AvailabilityService);
  private bestAvailableService = inject(BestAvailableService);
  private dvmService = inject(DvmService);
  private popover3dview = inject(Popover3dviewService);
  private tutorialService = inject(TutorialService);
  includedSectionForBA: boolean;
  sectionData: GeneralAvailability;
  subscriptions: Subscription[] = [];
  tooltipData: { section: string; img: string; type: string } = {
    section: '',
    img: '',
    type: '',
  };

  @Input() isTicketFeeEnabled: boolean = false;

  get price() {
    let firstPrice = this.sectionData?.priceRange[0].value;

    if (!this.sectionData) {
      return '';
    }

    const isFirstPriceInteger = Number.isInteger(firstPrice);
    const firstPriceFormatted = isFirstPriceInteger
      ? firstPrice.toString()
      : firstPrice.toFixed(2);

    const isPriceRange =
      this.sectionData.priceRange[0].value !=
      this.sectionData.priceRange[1].value;

    if (!isPriceRange) {
      return firstPriceFormatted;
    }

    return this.getPriceRange(firstPriceFormatted);
  }

  private getPriceRange(firstPriceFormatted: string) {
    const secondPrice = this.sectionData.priceRange[1].value;
    const isSecondPriceInteger = Number.isInteger(secondPrice);

    const secondPriceFormatted = isSecondPriceInteger
      ? secondPrice.toString()
      : secondPrice.toFixed(2);

    return `${firstPriceFormatted} - ${secondPriceFormatted}`;
  }

  get priceRangeWithFees() {
    if (!this.isTicketFeeEnabled || !this.sectionData) {
      return this.price;
    }

    const sectionId = this.sectionData.sectionID;
    const sectionSeats = this.availabilityService.seatsAvailability[sectionId];

    if (!sectionSeats) {
      return this.price;
    }

    const seatIds = Object.keys(sectionSeats);
    if (seatIds.length === 0) {
      return this.price;
    }

    const minPriceSeat = seatIds.find(seatId => {
      const seat = sectionSeats[seatId];
      return seat?.price?.[0]?.value === this.sectionData.priceRange[0].value;
    });

    const maxPriceSeat = seatIds.find(seatId => {
      const seat = sectionSeats[seatId];
      return seat?.price?.[0]?.value === this.sectionData.priceRange[1].value;
    });

    const minSeat = minPriceSeat ? sectionSeats[minPriceSeat] : null;
    const maxSeat = maxPriceSeat ? sectionSeats[maxPriceSeat] : null;

    if (!minSeat || !maxSeat) {
      return this.price;
    }

    const minPriceWithFees = minSeat.price[0].totalPriceWithFees;
    const maxPriceWithFees = maxSeat.price[0].totalPriceWithFees;

    if (minPriceWithFees === maxPriceWithFees) {
      return minPriceWithFees.toString();
    }

    return `${minPriceWithFees} - ${maxPriceWithFees}`;
  }

  get adaQuantity() {
    if (
      this.availabilityService.sectionsAvailability[
        this.sectionData.sectionID
      ] &&
      this.availabilityService.sectionsAvailability[this.sectionData.sectionID]
        .availableQuantityAda
    ) {
      return this.availabilityService.sectionsAvailability[
        this.sectionData.sectionID
      ].availableQuantityAda;
    } else if (
      this.availabilityService.sectionsOnlyAdas[this.sectionData.sectionID] &&
      this.availabilityService.sectionsOnlyAdas[this.sectionData.sectionID]
        .availableQuantityAda
    ) {
      return this.availabilityService.sectionsOnlyAdas[
        this.sectionData.sectionID
      ].availableQuantityAda;
    }
    return 0;
  }

  ngOnInit(): void {
    this.initializeComponent();
  }

  initializeComponent() {
    this.includedSectionForBA =
      this.bestAvailableService.selectedSectionsForBA.includes(
        this.sectionData.sectionID
      );

    this.tooltipData.section = this.sectionData.mmcID as string;

    let thumbSection = this.sectionData.sectionID;

    if (this.tooltipData.section === 'S_BLCHADA') {
      thumbSection = 'S_Bleacher';
    }

    const subscription = this.dvmService.loadThumbnail(thumbSection).subscribe({
      next: (img: HTMLImageElement) => (this.tooltipData.img = img.src),
      error: error => console.error(error),
    });
    this.subscriptions.push(subscription);
  }

  load3dView() {
    if (this.tutorialService.isOpen()) {
      return;
    }

    this.popover3dview.removeTooltip();
    this.popover3dview.deleteTimer();
    this.dvmService.load3dView(this.sectionData.sectionID);
  }

  onEnter(event: MouseEvent) {
    this.popover3dview.deleteTimer();
  }

  onLeave(event: MouseEvent) {
    this.popover3dview.setTimer();
  }

  openSeatMap() {
    if (this.tutorialService.isOpen()) {
      return;
    }

    const sectionId: string = this.sectionData.sectionID;

    const subscription = this.dvmService.openSeatMap(sectionId).subscribe();
    this.subscriptions.push(subscription);
    this.popover3dview.setTimer();
  }

  selectSectionForBA() {
    if (this.tutorialService.isOpen()) {
      return;
    }

    if (!this.includedSectionForBA) {
      this.bestAvailableService.selectSectionForBA(this.sectionData.sectionID);
    } else {
      this.bestAvailableService.unselectSectionForBA(
        this.sectionData.sectionID
      );
    }
    this.includedSectionForBA =
      this.bestAvailableService.selectedSectionsForBA.includes(
        this.sectionData.sectionID
      );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }
}
